import AutorenewIcon from "@mui/icons-material/Autorenew";
import DescriptionIcon from "@mui/icons-material/Description";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import DrawerComponentView from "src/app/components/drawer/drawer.view";
import DrawerAwareContainerComponentView from "src/app/components/drawerAwareContainer/drawerAwareContainer.view";
import SnackBarComponentView from "src/app/components/snackbar/snackbar.view";
import LogsScreenViewModel, {
  LogEntry,
} from "src/app/screens/logs/logs.viewmodel";
import styles from "src/app/styles/screens/logs.style";

const LogsScreenView: React.FC = () => {
  const {
    logs,
    pagination,
    isLoadingLogs,
    onPreviousPagePressed,
    onNextPagePressed,
    onRefreshPressed,
    formatDateTime,
    getLogTypeChipColor,
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    role,
    selectorUsers,
    isLoadingSelectorUsers,
    selectedLogsOwnerId,
    onSelectedLogsOwnerChanged,
  } = LogsScreenViewModel();

  return (
    <Box sx={styles.parent}>
      <DrawerComponentView selectedId="Logs" />

      <DrawerAwareContainerComponentView
        drawerWidth={250}
        sx={styles.drawerAwareContainer}
      >
        <Grid container justifyContent="center" spacing={3}>
          <Grid
            size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}
            sx={styles.titleGrid}
          >
            <Typography component="span" sx={styles.titleText}>
              Logs
            </Typography>
          </Grid>

          {(role === "Parent" || role === "Admin") && (
            <Grid container justifyContent="flex-end" mt={0} size={12}>
              <Grid size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}>
                <Paper elevation={0} sx={styles.selectorPaper}>
                  <FormControl fullWidth>
                    <InputLabel id="logs-owner-select-label">
                      Logs Owner
                    </InputLabel>
                    <Select
                      labelId="logs-owner-select-label"
                      value={selectedLogsOwnerId}
                      label="Logs Owner"
                      onChange={(event: SelectChangeEvent) =>
                        onSelectedLogsOwnerChanged(event.target.value)
                      }
                      disabled={isLoadingSelectorUsers}
                    >
                      <MenuItem value="all">All</MenuItem>
                      {selectorUsers.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          )}

          <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
            <Paper elevation={0} sx={styles.logsContainer}>
              <Box sx={styles.headerRow}>
                <Box>
                  <Typography sx={styles.sectionTitle}>System Logs</Typography>
                  <Typography sx={styles.subTitleText}>
                    Total Logs: {pagination.totalLogs}
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  startIcon={<AutorenewIcon />}
                  onClick={onRefreshPressed}
                  disabled={isLoadingLogs}
                >
                  Refresh
                </Button>
              </Box>

              {isLoadingLogs ? (
                <Box sx={styles.loaderContainer}>
                  <CircularProgress size={30} />
                </Box>
              ) : logs.length === 0 ? (
                <Typography sx={styles.noItemsText}>No logs found.</Typography>
              ) : (
                <Box sx={styles.logsListContainer}>
                  {logs.map((log: LogEntry) => (
                    <Paper key={log.id} elevation={0} sx={styles.logCard}>
                      <Box sx={styles.logCardHeader}>
                        <Box sx={styles.logHeaderLeft}>
                          <DescriptionIcon sx={styles.logIcon} />
                          <Typography sx={styles.logIdText}>
                            {log.id}
                          </Typography>
                        </Box>

                        <Chip
                          size="small"
                          label={log.type}
                          color={getLogTypeChipColor(log.type)}
                        />
                      </Box>

                      <Typography sx={styles.logCreatedAtText}>
                        {formatDateTime(log.createdAt)}
                      </Typography>

                      <Typography sx={styles.logText}>{log.text}</Typography>
                    </Paper>
                  ))}
                </Box>
              )}

              <Box sx={styles.paginationContainer}>
                <Button
                  variant="outlined"
                  onClick={onPreviousPagePressed}
                  disabled={!pagination.hasPreviousPage || isLoadingLogs}
                >
                  Previous
                </Button>

                <Typography sx={styles.paginationText}>
                  Page {pagination.page} of {pagination.totalPages || 1}
                </Typography>

                <Button
                  variant="outlined"
                  onClick={onNextPagePressed}
                  disabled={!pagination.hasNextPage || isLoadingLogs}
                >
                  Next
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DrawerAwareContainerComponentView>

      <SnackBarComponentView
        isError={isSnackbarError}
        isOpen={isDisplayingSnackbar}
        message={snackbarMessage}
        onClose={() => setIsDisplayingSnackbar(false)}
      />
    </Box>
  );
};

export default LogsScreenView;
